import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { queryClient } from '@/services/api/queryClient';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { UniformGallery } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { GetParamsObject } from '@/model/query-params';
import { UniformGalleryFilters } from './types';
import { UniformGalleryFilterBox } from './components/UniformGalleryFilterBox';
import { UniformGalleryTable } from './components/UniformGalleryTable/UniformGalleryTable';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useDeleteUniformGalleryMutation, useGetUniformGalleryById, useUniformGallery } from '@/services/queries/uniform-gallery';
import { UniformGalleryEditModal } from './components/UniformGalleryEditModal';
import { UniformGalleryCreateModal } from './components/UniformGalleryCreateModal';
import { ModalFormData as EditUniformSeasonModalFormData } from './components/UniformSeasonEditModal/schemas';
import { usePostUniformSeasonMutation, useUniformSeason } from '@/services/queries/uniform-season';
import { formatArrayToMultipleWordsSeparatedByComma } from '@/utils/string';
import { UniformSeasonEditModal } from './components/UniformSeasonEditModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationUniformGallery = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.UNIFORMS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_uniform_gallery.application_uniform_gallery_enable_success')
      : t('pages.application_uniform_gallery.application_uniform_gallery_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_uniform_gallery.application_uniform_gallery_enable_error')
      : t('pages.application_uniform_gallery.application_uniform_gallery_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.UNIFORMS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isOpenEditSeasonsModal, setIsOpenEditSeasonsModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState<UniformGallery | undefined>(undefined);

  const deleteMutation = useDeleteUniformGalleryMutation();
  const getItemByUUID = useGetUniformGalleryById();
  const { mutateAsync: createUniformSeason, isLoading: isLoadingCreateUniformSeason } = usePostUniformSeasonMutation();

  const { data: seasons, refetch: refetchSeason } = useUniformSeason();
  seasons?.sort((a, b) => a.title.localeCompare(b.title));

  const filtersForm = useFiltersForm<UniformGalleryFilters>({
    defaultValues: {
      season_uuid: '',
      team_uuid: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: clubs = [] } = useAllClub(true);
  const { data: itemData, isLoading, forceRefetch } = useUniformGallery(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: UniformGallery) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_uniform_gallery.application_uniform_gallery_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_uniform_gallery.application_uniform_gallery_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_uniform_gallery.application_uniform_gallery_not_found_error'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  const handleEditUniformSeason = async ({ titles }: EditUniformSeasonModalFormData) => {
    if (!titles) return;

    const response = await Promise.allSettled(
      titles.map(async item => {
        const response = await createUniformSeason({ title: item.name });
        return response.title;
      }),
    );

    const successItems = response
      ?.filter((item): item is PromiseFulfilledResult<string> => item.status === 'fulfilled')
      .map(item => item.value);

    const errorItems = response
      ?.filter((item): item is PromiseRejectedResult => item.status === 'rejected')
      .map(error => error.reason);

    if (errorItems.length > 0) {
      const positionsText =
        errorItems.length === 1
          ? t('pages.application_uniform_gallery.application_uniform_gallery_season_save_single_error')
          : t('pages.application_uniform_gallery.application_uniform_gallery_season_save_multiple_error');
      const errorsText =
        errorItems.length === 1 ? ` ${errorItems[0]}` : `: ${formatArrayToMultipleWordsSeparatedByComma(errorItems)}`;

      toast({
        title: `${positionsText}${errorsText}`,
        status: 'error',
      });

      await queryClient.invalidateQueries(['GET_UNIFORM_SEASON']);

      return;
    }

    if (successItems?.length > 0) {
      const successText =
        successItems.length === 1
          ? t('pages.application_uniform_gallery.application_uniform_gallery_season_save_single_success')
          : t('pages.application_uniform_gallery.application_uniform_gallery_season_save_multiple_success');

      toast({
        title: successText,
        status: 'success',
      });

      setIsOpenEditSeasonsModal(false);
      refetchSeason();
      await queryClient.invalidateQueries(['GET_UNIFORM_SEASON']);
    }
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent
      title={t('pages.application_uniform_gallery.application_uniform_gallery_page_title')}
      breadcrumbItems={breadcrumb}
    >
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_uniform_gallery.application_uniform_gallery_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_uniform_gallery.application_uniform_gallery_enabled')
                    : t('pages.application_uniform_gallery.application_uniform_gallery_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => {
                setIsCreateModalOpen(true);
              }}
              width={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_uniform_gallery.application_uniform_gallery_create_button')}
            </Button>

            <Button width={{ base: '100%', md: 'initial' }} onClick={() => setIsOpenEditSeasonsModal(true)}>
              {t('pages.application_uniform_gallery.application_uniform_gallery_edit_seasons_button')}
            </Button>
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <UniformGalleryFilterBox clubs={clubs} seasons={seasons} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <UniformGalleryTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <UniformGalleryEditModal
          seasons={seasons}
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <UniformGalleryCreateModal
        seasons={seasons}
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <UniformSeasonEditModal
        seasons={seasons}
        isOpen={isOpenEditSeasonsModal}
        onConfirm={handleEditUniformSeason}
        onClose={() => setIsOpenEditSeasonsModal(false)}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_uniform_gallery.application_uniform_gallery_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_uniform_gallery.application_uniform_gallery_delete_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_uniform_gallery.application_uniform_gallery_label')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_uniform_gallery.application_uniform_gallery_app_area_confirm')
              : t('pages.application_uniform_gallery.application_uniform_gallery_app_area_disabled')}
          </Text>
        }
      />

      <CenteredSpinner
        isLoading={
          getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading || isLoadingCreateUniformSeason
        }
      />
    </PageContent>
  );
};
